import { useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { SelectField } from '@/components/editor/EditorKit'
import {
  addCustomerNote,
  getLastTreatmentCard,
  listTreatmentCards,
  type IntakeAnswers,
  type IntakeBlock,
  type MultiSelectAnswer,
  type TreatmentCard,
  type TreatmentCardLast,
} from '@/lib/api'
import { readApiError } from '@/lib/apiError'
import { fmtDateTime } from '@/lib/format'
import { cardLines, isCardQuestion } from '@/lib/treatmentCard'
import { cn } from '@/lib/utils'

// ─── The treatment card, from the booking sheet ──────────────────────────────
//
// Two jobs in one dialog, and they are the two the operator asked for: SEE what
// was used last time, and RECORD what is being used today.
//
// PREFILLED FROM LAST TIME, the operator's explicit choice over starting blank.
// What contains the risk in that — an untouched field records a setting nobody
// chose today, and it reads exactly like one they did — is not a rule in this
// file but two things the surfaces keep saying: the header names the session
// the values came from and when, and the saved note carries the date it was
// actually written. The dropping of values a card no longer accepts is the
// SERVER's (carryForward), and arrives here as `prefill`; never re-derive it.
//
// Saving writes a CUSTOMER NOTE — the card id, the snapshot and the answers,
// with the text rendered server-side. So the history lands in the notes tab
// with no second feed, and both AI surfaces can already read it.
//
// OPERATOR-ONLY, like everything else about a card. This dialog only ever
// renders inside the booking sheet, which is itself gated on the calendar's
// `interactive` flag.

export interface TreatmentCardDialogProps {
  customer: { id: string; name: string } | null
  bookingId?: string
  serviceName?: string | null
  onOpenChange: (open: boolean) => void
  /** Called after a card is saved, so the sheet can re-read the last one. */
  onSaved?: () => void
}

export function TreatmentCardDialog({
  customer,
  bookingId,
  serviceName,
  onOpenChange,
  onSaved,
}: TreatmentCardDialogProps) {
  const open = customer !== null

  const [loading, setLoading] = useState(false)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [cards, setCards] = useState<TreatmentCard[]>([])
  const [cardId, setCardId] = useState<string | null>(null)
  const [state, setState] = useState<TreatmentCardLast | null>(null)
  const [answers, setAnswers] = useState<IntakeAnswers>({})
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Which cards exist. Archived ones are excluded by the API, and a draft
  // cannot be filled in, so only active cards are offered.
  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLoading(true)
    setLoadError(null)
    listTreatmentCards({ limit: 100 })
      .then((page) => {
        if (cancelled) return
        const usable = page.items.filter((c) => c.status === 'active')
        setCards(usable)
        setCardId((prev) => prev ?? usable.find((c) => c.is_default)?.id ?? usable[0]?.id ?? null)
        if (usable.length === 0) setLoading(false)
      })
      .catch((e) => {
        if (cancelled) return
        setLoadError(readApiError(e))
        setLoading(false)
      })
    return () => { cancelled = true }
  }, [open])

  // The chosen card, this client's last one, and what today opens with — one
  // round trip, because the sheet needs all three at once.
  useEffect(() => {
    if (!open || !cardId || !customer) return
    let cancelled = false
    setLoading(true)
    setError(null)
    getLastTreatmentCard(cardId, customer.id)
      .then((res) => {
        if (cancelled) return
        setState(res)
        setAnswers(res.prefill ?? {})
      })
      .catch((e) => { if (!cancelled) setLoadError(readApiError(e)) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [open, cardId, customer?.id])

  // Reset when the sheet closes, so the next booking does not open holding the
  // previous client's readings.
  useEffect(() => {
    if (open) return
    setState(null)
    setAnswers({})
    setCardId(null)
    setError(null)
  }, [open])

  const blocks = state?.card.document?.blocks ?? []
  const lastLines = useMemo(
    () => cardLines(state?.card.document, state?.last?.answers),
    [state],
  )

  const setAnswer = (id: string, value: unknown) => {
    setAnswers((prev) => {
      const next = { ...prev }
      if (value === undefined || value === '') delete next[id]
      else next[id] = value
      return next
    })
  }

  async function save() {
    if (!customer || !cardId) return
    setSaving(true)
    setError(null)
    try {
      await addCustomerNote(customer.id, {
        treatment_card_id: cardId,
        answers,
        ...(bookingId ? { booking_id: bookingId } : {}),
      })
      toast.success('Treatment card saved to their notes')
      onSaved?.()
      onOpenChange(false)
    } catch (e) {
      setError(readApiError(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Treatment card</DialogTitle>
          <DialogDescription>
            {customer?.name}{serviceName ? ` · ${serviceName}` : ''} — what was delivered today.
            It saves to their notes.
          </DialogDescription>
        </DialogHeader>

        {loadError ? (
          <p className="text-[12.5px] text-destructive">{loadError}</p>
        ) : cards.length === 0 && !loading ? (
          <p className="text-[12.5px] text-muted-foreground">
            No active treatment cards yet. Build one under Catalogue → Treatment cards, then it
            can be filled in from here.
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {cards.length > 1 ? (
              <SelectField
                value={cardId ?? ''}
                onChange={(v) => setCardId(v)}
                disabled={saving}
                options={cards.map((c) => ({
                  value: c.id,
                  label: c.is_default ? `${c.name} (usual)` : c.name,
                }))}
              />
            ) : null}

            {loading ? (
              <div className="flex justify-center py-6 text-muted-foreground">
                <Loader2 className="size-4 animate-spin" aria-hidden />
              </div>
            ) : (
              <>
                {/* WHERE THE VALUES CAME FROM. Prefilled fields are only safe
                    if this line is here — it is what stops a carried-over
                    reading reading as one taken today. */}
                {state?.last ? (
                  <div className="rounded-lg border border-border bg-muted/40 p-2.5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted-foreground">
                      Last time · {fmtDateTime(state.last.created_at)}
                    </p>
                    {lastLines.length > 0 ? (
                      <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-2 gap-y-0.5">
                        {lastLines.map((l) => (
                          <div key={l.block_id} className="contents">
                            <dt className="text-[12px] text-muted-foreground">{l.label}</dt>
                            <dd className="text-[12px] font-semibold text-foreground">{l.value}</dd>
                          </div>
                        ))}
                      </dl>
                    ) : (
                      <p className="mt-1 text-[12px] text-muted-foreground">Nothing was filled in.</p>
                    )}
                    <p className="mt-1.5 text-[11px] text-muted-foreground/80">
                      Today's fields start from these — change anything that is different.
                    </p>
                  </div>
                ) : (
                  <p className="text-[12px] text-muted-foreground">
                    First time on this card — nothing to carry over.
                  </p>
                )}

                <div className="flex flex-col gap-2.5">
                  {blocks.map((block) => (
                    <CardField
                      key={block.id}
                      block={block}
                      value={answers[block.id]}
                      disabled={saving}
                      onChange={(v) => setAnswer(block.id, v)}
                    />
                  ))}
                </div>

                {error ? <p className="text-[12.5px] text-destructive">{error}</p> : null}

                <button
                  type="button"
                  disabled={saving || !cardId}
                  onClick={() => void save()}
                  className="mt-1 w-full rounded-lg bg-primary px-3 py-2 text-[13px] font-semibold text-primary-foreground disabled:opacity-50"
                >
                  {saving ? 'Saving…' : 'Save to their notes'}
                </button>
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** One field of the card. The vocabulary is _shared/intake-doc.ts. */
function CardField({
  block,
  value,
  disabled,
  onChange,
}: {
  block: IntakeBlock
  value: unknown
  disabled: boolean
  onChange: (value: unknown) => void
}) {
  if (!isCardQuestion(block.type)) {
    return block.type === 'heading' ? (
      <p className="mt-1 text-[12px] font-bold uppercase tracking-[0.5px] text-muted-foreground">
        {block.label}
      </p>
    ) : (
      <p className="text-[12px] text-muted-foreground">{block.label}</p>
    )
  }

  const label = (
    <label className="mb-1 block text-[11.5px] font-semibold text-foreground">
      {block.label}
      {block.required ? <span className="ml-1 text-rose-600">*</span> : null}
      {block.type === 'number' && block.unit ? (
        <span className="ml-1 font-normal text-muted-foreground">({block.unit})</span>
      ) : null}
    </label>
  )

  switch (block.type) {
    case 'number':
      return (
        <div>
          {label}
          <Input
            type="number"
            inputMode="decimal"
            value={value === undefined || value === null ? '' : String(value)}
            min={block.min}
            max={block.max}
            disabled={disabled}
            // Kept as a STRING until it is a finite number: an empty box must
            // clear the answer, not record NaN as a setting.
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') return onChange(undefined)
              const num = Number(raw)
              onChange(Number.isFinite(num) ? num : raw)
            }}
          />
        </div>
      )
    case 'textarea':
      return (
        <div>
          {label}
          <Textarea
            rows={3}
            value={typeof value === 'string' ? value : ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )
    case 'yes_no':
      return (
        <div>
          {label}
          <div className="flex gap-1.5">
            {(['yes', 'no'] as const).map((v) => (
              <Chip
                key={v}
                on={value === v}
                disabled={disabled}
                label={v === 'yes' ? 'Yes' : 'No'}
                onClick={() => onChange(value === v ? undefined : v)}
              />
            ))}
          </div>
        </div>
      )
    case 'select':
      return (
        <div>
          {label}
          <div className="flex flex-wrap gap-1.5">
            {(block.options ?? []).map((o) => (
              <Chip
                key={o}
                on={value === o}
                disabled={disabled}
                label={o}
                onClick={() => onChange(value === o ? undefined : o)}
              />
            ))}
          </div>
        </div>
      )
    case 'multi_select': {
      const selected = ((value as Partial<MultiSelectAnswer>)?.selected ?? []) as string[]
      const note = (value as Partial<MultiSelectAnswer>)?.note
      const emit = (next: string[], nextNote?: string) => {
        if (next.length === 0 && !nextNote) return onChange(undefined)
        const answer: MultiSelectAnswer = { selected: next }
        if (nextNote) answer.note = nextNote
        onChange(answer)
      }
      return (
        <div>
          {label}
          <div className="flex flex-wrap gap-1.5">
            {(block.options ?? []).map((o) => (
              <Chip
                key={o}
                on={selected.includes(o)}
                disabled={disabled}
                label={o}
                onClick={() => emit(
                  selected.includes(o) ? selected.filter((s) => s !== o) : [...selected, o],
                  note,
                )}
              />
            ))}
          </div>
          {block.note_prompt && selected.length > 0 ? (
            <Input
              className="mt-1.5"
              placeholder={block.note_prompt}
              value={note ?? ''}
              disabled={disabled}
              onChange={(e) => emit(selected, e.target.value || undefined)}
            />
          ) : null}
        </div>
      )
    }
    default:
      return (
        <div>
          {label}
          <Input
            value={typeof value === 'string' ? value : ''}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
          />
        </div>
      )
  }
}

function Chip({
  on, label, disabled, onClick,
}: {
  on: boolean
  label: string
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={on}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'rounded-full border px-2.5 py-1 text-[11.5px] font-semibold transition-colors',
        on ? 'border-primary bg-primary/10 text-foreground' : 'border-border bg-card text-muted-foreground',
      )}
    >
      {label}
    </button>
  )
}
